import React, { useContext, useState, useEffect, useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import SquareHeartFilledSmall from '@wix/da-ds/pkg/Icons/16x16/SquareHeartFilled';
import SquareHeartOutlinedSmall from '@wix/da-ds/pkg/Icons/16x16/SquareHeartOutlined';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { BiDataHook, BiEventType } from '@wix/da-bi/pkg/constants';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import CustomTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/CustomTooltip';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import { CommentContext } from '../../CommentContext';
import ReactionList from '../ReactionList';
import ReactionsDisplay from '../ReactionsDisplay';
import s from './LikeButton.scss';
var LikeButton = function (_a) {
    var _b;
    var className = _a.className, disabled = _a.disabled, onLike = _a.onLike, onUnlike = _a.onUnlike;
    var t = useTranslation().t;
    var _c = useState(false), isHovered = _c[0], setIsHovered = _c[1];
    var _d = useContext(CommentContext), comment = _d.comment, isReduxReady = _d.isReduxReady;
    var comment_reactions = useFlag('comment_reactions');
    var _e = useState(true), isSSR = _e[0], setIsSSR = _e[1];
    useEffect(function () {
        setIsSSR(typeof window === 'undefined');
    }, []);
    // Disable comment reactions in SSR because the data comes from local storage
    var commentReactionsEnabled = comment_reactions && !isSSR;
    var onMouseEnter = function () { return setIsHovered(true); };
    var onMouseLeave = function () { return setIsHovered(false); };
    var onClickLike = useCallback(function () {
        // don't allow liking your own comment
        if (comment.isAuthor) {
            return;
        }
        if (comment.isLiked) {
            onUnlike(comment);
            // Unlike provokes a re-render, which will show the tooltip again unless we set hovered state off.
            if (commentReactionsEnabled) {
                setIsHovered(false);
            }
        }
        else {
            onLike(comment);
        }
    }, [comment, onLike, onUnlike, commentReactionsEnabled]);
    var isAuthor = comment.isAuthor, isLiked = comment.isLiked, likes = comment.likes;
    var canLike = !isAuthor;
    var hasLikes = likes > 0;
    var HeartIconSmall = isLiked
        ? SquareHeartFilledSmall
        : SquareHeartOutlinedSmall;
    var heartClasses = classnames(s['like'], isAuthor && s['not-clickable']);
    return (
    // Wrapper div because SimpleTooltip can render two elements at its root which will throw
    // off grid and flex layouts
    React.createElement("div", { className: className, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave },
        React.createElement(PlainButtonOrLink, { className: s['likebutton'], onClick: onClickLike, disabled: disabled !== null && disabled !== void 0 ? disabled : !isReduxReady, "data-hook": BiDataHook.COMMENT_LIKE_BUTTON, biData: canLike
                ? {
                    evid: BiEventType.CLICK_COMMENT_LIKE,
                    commenterid: (_b = comment.user) === null || _b === void 0 ? void 0 : _b.useridUuid,
                    commentid: comment.commentId,
                    typeid: comment.typeId,
                    itemid: comment.itemId,
                    is_liking: isLiked ? undefined : 1,
                }
                : undefined, "aria-label": t('comment.a11y.action.like') },
            commentReactionsEnabled &&
                (canLike && !isLiked ? (React.createElement(CustomTooltip, { forceVisible: isHovered, offsetModifier: [0, 2], placement: "top", secondaryPlacement: "start", usePortal: true, content: React.createElement(ReactionList, { comment: comment }) }, hasLikes ? (React.createElement(ReactionsDisplay, { className: s['reactions'], comment: comment })) : (React.createElement(HeartIconSmall, { className: heartClasses, size: IconSize.SMALLER, disableDefaultColors: true })))) : (React.createElement(ReactionsDisplay, { className: s['reactions'], comment: comment }))),
            !commentReactionsEnabled && (React.createElement(SimpleTooltip, { forceVisible: isHovered, offsetModifier: [0, 2], usePortal: true, text: isLiked
                    ? t('comment.action.like.tooltip.liked')
                    : t('comment.action.like.tooltip.notLiked') },
                React.createElement(HeartIconSmall, { className: heartClasses, size: IconSize.SMALLER, disableDefaultColors: true }))),
            !commentReactionsEnabled && hasLikes && (React.createElement("span", { className: s['likes'] },
                shortenNumber(likes),
                React.createElement(VisuallyHidden, null, t('comment.action.likes', { count: likes })))))));
};
LikeButton.displayName = 'LikeButton';
export default LikeButton;
//# sourceMappingURL=LikeButton.js.map